import cloneDeep from "lodash/cloneDeep"

/**
 * A convenience function that ensures a workspace entry exists in a slice's byWorkspace
 * map, initializing it with a default shape if absent. Intended to be partially
 * applied per-slice to create a slice-specific `ensureWorkspace()` helper.
 *
 * Must only be called inside a Redux Toolkit reducer (Immer-wrapped) as it
 * mutates state directly.
 *
 * @param {Object} state - The Immer draft state of the slice. Must contain a
 *   byWorkspace property.
 * @param {string|number} workspaceId - The ID of the workspace to ensure exists.
 * @param {Object} [defaultWorkspaceShape={}] - The default state shape for a
 *   new workspace entry. A shallow copy is created on initialization, so nested
 *   objects in the default should be empty literals to avoid shared references.
 * @returns {Object} The Immer draft reference for the workspace entry, safe to
 *   mutate within the calling reducer.
 *
 * @example
 * // In a slice file, create a slice-specific helper:
 * const ensureWorkspace = (state, workspaceId) =>
 *   ensureWorkspaceTemplate(state, workspaceId, { object_representing_an_inital_state })
 *
 * // Then use inside reducers:
 * setLoading: (state, action) => {
 *   const { workspaceId } = action.payload
 *   const workspace = ensureWorkspace(state, workspaceId)
 *   workspace.loading = true
 * }
 */
export const ensureWorkspaceTemplate = (state, workspaceId, defaultWorkspaceShape = {}) => {
	if(!state.byWorkspace[workspaceId]) {
		state.byWorkspace[workspaceId] = cloneDeep(defaultWorkspaceShape)
	}
	return state.byWorkspace[workspaceId]
}

/**
 * helper function to marshall recent-articles keys to always use the same syntax
 *
 * @param {number || string} workspaceId
 * @returns {`recently-viewed-articles-${string}`}
 */
export const getRecentArticlesKey = (workspaceId) => `recently-viewed-articles-${workspaceId}`